import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ShoppingCart, ArrowLeft, Menu, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useOrder } from '@/contexts/OrderContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import MenuItem from '@/components/MenuItem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const { getCartCount } = useCart();
  const { orderType } = useOrder();
  const navigate = useNavigate();
  const categoryRefs = useRef({});

  useEffect(() => {
    fetchMenu();
    fetchCategories();
  }, []);

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API}/menu`);
      setMenuItems(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching menu:', error);
      toast.error('Failed to load menu');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/menu/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const scrollToCategory = (category) => {
    setSelectedCategory(category);
    setShowCategoryMenu(false);
    if (category === 'all') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (categoryRefs.current[category]) {
      const element = categoryRefs.current[category];
      const offset = 150;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = searchQuery === '' || item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  // Get item count per category
  const getCategoryCount = (category) => {
    return menuItems.filter(item => item.category === category).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-md border-b border-amber-500/30 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/')} variant="ghost" className="text-amber-400 hover:bg-amber-500/10 p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img 
              src="/spicifi-logo.png" 
              alt="SPICIFI" 
              className="h-12 w-auto object-contain"
            />
            <div>
              <p className="text-xs text-amber-300 capitalize font-semibold">{orderType} Order</p>
            </div>
          </div>
          <Button onClick={() => navigate('/cart')} className="relative bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
            <ShoppingCart className="w-5 h-5 mr-2" />
            Cart
            {getCartCount() > 0 && (
              <Badge className="absolute -top-2 -right-2 bg-green-500 px-2 py-0.5">{getCartCount()}</Badge>
            )}
          </Button>
        </div>

        <div className="max-w-7xl mx-auto px-4 pb-3">
          <div className="relative">
            <Input
              type="text"
              placeholder="Search dishes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 bg-gray-800 border-amber-500/30 text-white placeholder:text-gray-400 focus:border-amber-500"
            />
          </div>
        </div>
      </header>

      {/* Menu Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 pb-24">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
            <p className="mt-4 text-amber-200">Loading menu...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-amber-200 text-lg">No items found</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedItems).map(([category, items]) => (
              <div 
                key={category} 
                ref={el => categoryRefs.current[category] = el}
                className="space-y-4"
                data-testid={`category-${category.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <h3 className="text-2xl font-bold text-amber-400 pb-2 border-b-2 border-amber-500/30 font-['Playfair_Display'] sticky top-24 bg-gray-900/95 backdrop-blur-md z-40 py-2">
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map(item => (
                    <MenuItem key={item.id} item={item} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Floating Menu Toggle Button */}
      <button
        onClick={() => setShowCategoryMenu(true)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 hover:from-amber-600 hover:to-orange-700 transition-all duration-300 hover:scale-105"
        data-testid="menu-toggle-btn"
      >
        <Menu className="w-5 h-5" />
        <span className="font-semibold">Menu</span>
      </button>

      {/* Category Menu Overlay */}
      {showCategoryMenu && (
        <div className="fixed inset-0 z-[100]" data-testid="category-menu-overlay">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowCategoryMenu(false)}
          />
          
          {/* Category Menu Panel */}
          <div className="absolute bottom-0 left-0 right-0 bg-gray-900 rounded-t-3xl max-h-[70vh] overflow-hidden animate-slide-up border-t-2 border-amber-500/50">
            {/* Header */}
            <div className="sticky top-0 bg-gray-900 px-4 py-4 border-b border-amber-500/30 flex items-center justify-between">
              <h2 className="text-xl font-bold text-amber-400 font-['Playfair_Display']">Browse Menu</h2>
              <button 
                onClick={() => setShowCategoryMenu(false)}
                className="p-2 rounded-full bg-gray-800 text-amber-400 hover:bg-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Category List */}
            <div className="overflow-y-auto max-h-[calc(70vh-80px)] pb-6">
              {/* All Items */}
              <button
                onClick={() => scrollToCategory('all')}
                className={`w-full px-4 py-4 flex items-center justify-between border-b border-gray-800 transition-colors ${
                  selectedCategory === 'all' ? 'bg-amber-500/20' : 'hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🍽️</span>
                  <div className="text-left">
                    <p className={`font-semibold ${selectedCategory === 'all' ? 'text-amber-400' : 'text-white'}`}>All Items</p>
                    <p className="text-sm text-gray-400">{menuItems.length} items</p>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 ${selectedCategory === 'all' ? 'text-amber-400' : 'text-gray-500'}`} />
              </button>

              {/* Categories */}
              {categories.map((category, index) => {
                const emoji = getCategoryEmoji(category);
                const count = getCategoryCount(category);
                return (
                  <button
                    key={category}
                    onClick={() => scrollToCategory(category)}
                    className={`w-full px-4 py-4 flex items-center justify-between border-b border-gray-800 transition-colors ${
                      selectedCategory === category ? 'bg-amber-500/20' : 'hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{emoji}</span>
                      <div className="text-left">
                        <p className={`font-semibold ${selectedCategory === category ? 'text-amber-400' : 'text-white'}`}>{category}</p>
                        <p className="text-sm text-gray-400">{count} items</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${selectedCategory === category ? 'text-amber-400' : 'text-gray-500'}`} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Helper function to get emoji for category
const getCategoryEmoji = (category) => {
  const emojiMap = {
    'South Indian Veg - Idly': '🥣',
    'South Indian Veg - Vada': '🍩',
    'South Indian Veg - Benne Dosa': '🥞',
    'South Indian Veg - Dosa': '🥞',
    'South Indian Veg - Rava Dosa': '🥞',
    'South Indian Veg - Utthapam': '🥞',
    'South Indian Veg - Pesarattu': '🥬',
    'South Indian Veg - Others': '🍛',
    'Special Rices': '🍚',
    'Meals': '🍱',
    'South Indian Non-Veg Breakfast': '🍳',
    'South Indian Non-Veg Meals': '🍖',
    'Biryani - Veg': '🍚',
    'Biryani - Non Veg': '🍗',
    'Veg Starters': '🥗',
    'Veg Tandoor Starters': '🧀',
    'Veg Soup': '🥣',
    'Non-Veg Starters': '🍖',
    'Non-Veg Tandoor Starters': '🍢',
    'Non-Veg Soup': '🥣',
    'North Indian Veg Main Course': '🥘',
    'North Indian Non-Veg Main Course': '🍛',
    'Indian Breads': '🫓',
    'Veg Fried Rice': '🍚',
    'Non-Veg Fried Rice': '🍚',
    'Regular Veg Thali': '🍱',
    'Punjabi Veg Thali': '🍱',
    'Regular Non-Veg Thali': '🍱',
    'Punjabi Non-Veg Thali': '🍱',
    'Chinese Combo': '🥡',
    'Fresh Juices': '🧃',
    'Desserts': '🍨',
  };
  return emojiMap[category] || '🍽️';
};

export default HomePage;
